import { CeremonyStateBadge as StateBadge } from '../components/ceremony-state-badge';
import { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  ApiNotConfiguredError,
  createCeremony,
  listCeremonies,
  type CeremonySummary,
} from '../../lib/witness-api';
import type { TenantOutletContext } from './tenant-layout';

import { CeremonyCreationForm } from '../components/ceremony-creation-form';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; ceremonies: readonly CeremonySummary[] };

export function TenantCeremoniesPage() {
  const { tenant } = useOutletContext<TenantOutletContext>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const reload = useCallback(
    (signal?: AbortSignal) => {
      setState({ status: 'loading' });
      listCeremonies(tenant.id, {}, signal ? { signal } : {})
        .then((ceremonies) => setState({ status: 'ready', ceremonies }))
        .catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          const message =
            err instanceof ApiNotConfiguredError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'failed to load ceremonies';
          setState({ status: 'error', message });
        });
    },
    [tenant.id],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="font-serif text-xl tracking-tight text-neutral-900">
          Ceremonies
        </h2>
        <p className="mt-1 text-sm text-neutral-600">
          Ceremonies in flight, returned, completed, or failed for {tenant.displayName}.
        </p>
      </div>

      <CeremonyCreationForm tenantId={tenant.id} submitCeremony={async (input) => {
        const result = await createCeremony(tenant.id, input);
        if (!result.ok) throw new Error({ unknown_template: 'Unknown template.',
          no_eligible_card: 'Payment pool has no eligible card.', invalid_input: 'Request rejected as invalid.',
          server_error: 'Create failed.' }[result.reason]);
      }} onCreated={() => reload()} />

      <CeremoniesList
        tenantId={tenant.id}
        state={state}
        onRetry={() => reload()}
      />
    </div>
  );
}

function CeremoniesList({
  tenantId,
  state,
  onRetry,
}: {
  tenantId: string;
  state: LoadState;
  onRetry: () => void;
}) {
  if (state.status === 'loading') {
    return <p className="text-sm text-neutral-500">Loading ceremonies…</p>;
  }
  if (state.status === 'error') {
    return (
      <div className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm">
        <p className="text-red-900">
          Failed to load ceremonies: {state.message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-red-900 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }
  if (state.ceremonies.length === 0) {
    return <p className="text-sm text-neutral-500">No ceremonies yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
            <th className="py-2 pr-4">Id</th>
            <th className="py-2 pr-4">Template</th>
            <th className="py-2 pr-4">State</th>
            <th className="py-2 pr-4">Created</th>
            <th className="py-2 pr-4">Expires</th>
          </tr>
        </thead>
        <tbody>
          {state.ceremonies.map((ceremony) => (
            <tr key={ceremony.id} className="border-b border-neutral-100">
              <td className="py-2 pr-4 font-mono text-xs text-neutral-700">
                <Link
                  to={`/t/${tenantId}/ceremonies/${ceremony.id}`}
                  className="text-neutral-900 hover:underline"
                >
                  {ceremony.id.slice(0, 8)}…
                </Link>
              </td>
              <td className="py-2 pr-4 text-neutral-700">
                {ceremony.templateId}
              </td>
              <td className="py-2 pr-4">
                <StateBadge state={ceremony.state} />
              </td>
              <td className="py-2 pr-4 text-neutral-500">
                {formatDate(ceremony.createdAtMs)}
              </td>
              <td className="py-2 pr-4 text-neutral-500">
                {formatDate(ceremony.expiresAtMs)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDate(ms: number): string {
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}
